import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import { Alert, Box, Paper } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	HISTORICAL_SAMPLE_DIALOG_TITLE,
	HISTORICAL_SAMPLE_WARNING_MESSAGE,
} from "../../shared/historicalSample";
import {
	getCurrentSection,
	useSections,
} from "@/shared/selectors/SectionsOfPartSelector";

import { InspectionFormSplitLayout } from "./InspectionFormSplitLayout";
import { RollingGrid } from "./RollingGrid";
import { SectionDetailsPanel } from "./SectionDetailsPanel";
import type {
	CreateInspectionReset,
	InspectionFormSubmission,
} from "../../models/InspectionForm";
import { useInspectionByIdQuery } from "../../hooks/queries/useInspectionByIdQuery";
import { useInspectionUrl } from "../../hooks/useInspectionUrl";
import {
	SECTION_SPEC_STATUS,
	type SectionSpecDetail,
} from "@/shared/models/SectionSpec";
import { useInspectionsQuery } from "../../hooks/queries/useInspectionsQuery";
import { useRollingSummaryQuery } from "../../hooks/queries/useRollingSummaryQuery";
import type {
	InspectionWithValues,
	UpsertInspectionPayload,
} from "../../models/Inspection";
import {
	useDeleteInspectionMutation,
	useUpsertInspectionMutation,
} from "../../hooks/mutations/useInspectionMutations";

interface Props {
	assetId: number;
}

const INSPECTION_PANEL_ID = "new-inspection";
const DETAILS_PANEL_ID = "section-details";
const TEST_SHACK_LAYOUT_ID = "test-shack-workspace";
const INITIAL_CREATE_RESET: CreateInspectionReset = {
	version: 0,
};
const CONFIRM_DIALOG_VALUE = "OK";
const CURRENT_SHIFT_CHECK_ERROR =
	"The current shift could not be verified. The inspection was not saved.";

export const TestShackWorkspace = ({ assetId }: Props) => {
	const [inspectionUrl, setInspectionUrl] = useInspectionUrl();
	const inspectionById = useInspectionByIdQuery(assetId);

	const [editing, setEditing] = useState<InspectionWithValues | null>(null);
	const [sectionId, setSectionId] = useState<number | null>(null);
	const [specDetail, setSpecDetail] = useState<SectionSpecDetail | null>(null);
	const [specPending, setSpecPending] = useState(true);
	const handleSpecChange = useCallback(
		(detail: SectionSpecDetail | null, pending: boolean) => {
			setSpecDetail(detail);
			setSpecPending(pending);
		},
		[],
	);
	const [createReset, setCreateReset] = useState(INITIAL_CREATE_RESET);
	const [toDelete, setToDelete] = useState<InspectionWithValues | null>(null);
	const [pendingHistoricalSave, setPendingHistoricalSave] =
		useState<UpsertInspectionPayload | null>(null);
	const [error, setError] = useState("");
	const submitInProgressRef = useRef(false);
	const pendingHistoricalSaveRef = useRef<UpsertInspectionPayload | null>(null);

	useEffect(() => {
		if (inspectionUrl !== null) return;
		setEditing(null);
		setCreateReset((current) => ({ version: current.version + 1 }));
	}, [inspectionUrl]);
	useEffect(() => {
		// Restore on entry or ID changes, not on same-ID background refreshes.
		if (
			!inspectionById.data ||
			editing?.ProfileInspectionId === inspectionById.data.ProfileInspectionId
		)
			return;
		setSectionId(inspectionById.data.SectionId);
		setEditing(inspectionById.data);
	}, [inspectionById.data, editing?.ProfileInspectionId]);
	const urlBlocked =
		inspectionById.requested &&
		editing?.ProfileInspectionId !== Number(inspectionUrl);
	const onError = (e: Error) => {
		setError(e.message);
	};
	const resetCreateForm = () => {
		setCreateReset((current) => ({
			version: current.version + 1,
		}));
	};

	const sectionsQuery = useSections({
		refetchInterval: 60_000,
		assetId,
		onError,
	});
	const currentRollingSection = getCurrentSection(sectionsQuery.data);
	const currentSectionId = currentRollingSection?.SectionId ?? null;
	const currentRollingSectionLabel = currentRollingSection?.SectionName ?? "";
	const inspectionsQuery = useInspectionsQuery({ assetId, onError });
	const rollingQuery = useRollingSummaryQuery({
		assetId,
		sectionId: currentSectionId,
		onError,
	});

	const saveMutation = useUpsertInspectionMutation({
		onSuccess: () => {
			void setInspectionUrl(null);
			setEditing(null);
			resetCreateForm();
		},
		onError,
	});

	const deleteMutation = useDeleteInspectionMutation({
		onSuccess: (profileInspectionId) => {
			if (editing?.ProfileInspectionId === profileInspectionId) {
				void setInspectionUrl(null);
				setEditing(null);
				resetCreateForm();
			}
		},
		onError,
	});

	const saveSnapshot = (snapshot: UpsertInspectionPayload) => {
		saveMutation.mutate(snapshot, {
			onSettled: () => {
				submitInProgressRef.current = false;
			},
		});
	};

	const handleInspectionSubmit = (payload: InspectionFormSubmission) => {
		if (urlBlocked || submitInProgressRef.current || saveMutation.isPending)
			return;

		const snapshot: UpsertInspectionPayload = {
			profileInspectionId: editing?.ProfileInspectionId ?? null,
			assetId,
			sectionId: payload.sectionId,
			heatId: payload.heatId,
			inspectedAt: new Date(payload.inspectedAt),
			heatName: null,
			measuredLbsPerFoot: payload.measuredLbsPerFoot,
			weightProfileItemId: specDetail?.weightProfileItemId ?? null,
			comments: payload.comments,
			readings: payload.readings.map((reading) => ({ ...reading })),
		};

		submitInProgressRef.current = true;
		const currentShift = inspectionsQuery.data?.currentShift;
		if (!currentShift) {
			submitInProgressRef.current = false;
			onError(new Error(CURRENT_SHIFT_CHECK_ERROR));
			return;
		}

		if (snapshot.inspectedAt.getTime() < currentShift.Start.getTime()) {
			pendingHistoricalSaveRef.current = snapshot;
			setPendingHistoricalSave(snapshot);
			return;
		}

		saveSnapshot(snapshot);
	};

	const handleHistoricalSaveClose = (value?: string) => {
		const snapshot = pendingHistoricalSaveRef.current;
		pendingHistoricalSaveRef.current = null;
		setPendingHistoricalSave(null);

		if (value === CONFIRM_DIALOG_VALUE && snapshot) {
			saveSnapshot(snapshot);
			return;
		}

		submitInProgressRef.current = false;
	};

	const spec =
		specDetail?.spec?.SectionId === sectionId ? specDetail.spec : null;
	const specItems = spec
		? (specDetail?.items.filter((item) => item.Applies) ?? [])
		: [];
	const hasConfirmedRevisions =
		specDetail?.versions.some(
			(version) => version.StatusCode !== SECTION_SPEC_STATUS.DRAFT,
		) ?? false;
	const formOptions = {
		assetId,
		sectionId,
		currentSectionId,
		onSpecChange: handleSpecChange,
		blocked: urlBlocked,
		editing,
		createReset,
		saving: pendingHistoricalSave !== null || saveMutation.isPending,
		onCancelEdit: () => {
			void setInspectionUrl(null);
			setEditing(null);
			resetCreateForm();
		},
		onSectionChange: setSectionId,
		onSubmit: handleInspectionSubmit,
	};
	const detailsPanel = (
		<Paper elevation={2} sx={{ p: 2, height: "100%", overflow: "auto" }}>
			<SectionDetailsPanel
				sectionId={sectionId}
				spec={spec}
				pending={urlBlocked || specPending}
				hasConfirmedRevisions={hasConfirmedRevisions}
			/>
		</Paper>
	);

	return (
		<>
			<Box
				sx={{
					display: "grid",
					gridTemplateRows: "minmax(480px, 1fr) auto",
					gap: 1,
					height: "100%",
					minHeight: 0,
					overflow: "auto",
				}}
			>
				<Box sx={{ minHeight: 0, overflow: "auto" }}>
					<InspectionFormSplitLayout
						formOptions={formOptions}
						formHeader={
							urlBlocked &&
							inspectionById.error && (
								<Alert severity="error">{inspectionById.error}</Alert>
							)
						}
						detailsPanel={detailsPanel}
						layoutId={TEST_SHACK_LAYOUT_ID}
						formPanelId={INSPECTION_PANEL_ID}
						detailsPanelId={DETAILS_PANEL_ID}
					/>
				</Box>
				<Box>
					<RollingGrid
						rows={inspectionsQuery.data?.inspections ?? []}
						applicableItemsBySpec={inspectionsQuery.data?.applicableItemsBySpec}
						currentRollingSectionLabel={currentRollingSectionLabel}
						specItems={specItems}
						summary={rollingQuery.data?.summary ?? null}
						summaryDimensions={rollingQuery.data?.dimensions ?? []}
						onNew={() => {
							void setInspectionUrl(null);
							setEditing(null);
							resetCreateForm();
						}}
						onEdit={(inspection) => {
							void setInspectionUrl(String(inspection.ProfileInspectionId));
						}}
						onDelete={setToDelete}
					/>
				</Box>
			</Box>

			<ConfirmationDialogRaw
				data-test-id="historical-inspection-dialog"
				open={pendingHistoricalSave !== null}
				title={HISTORICAL_SAMPLE_DIALOG_TITLE}
				message={HISTORICAL_SAMPLE_WARNING_MESSAGE}
				buttons={{ no: "Review date", yes: "Save anyway" }}
				onClose={handleHistoricalSaveClose}
			/>

			<ConfirmationDialogRaw
				data-test-id="delete-inspection-dialog"
				open={toDelete !== null}
				title="Delete inspection"
				message={
					toDelete
						? `Delete the sample of heat ${toDelete.HeatName ?? "-"} taken on ${dxtToLocalServerTime(
								toDelete.InspectedAt,
								"dd MMM yyyy HH:mm",
							)}?`
						: ""
				}
				buttons={{ no: "Cancel", yes: "Delete" }}
				onClose={(value?: string) => {
					if (value === CONFIRM_DIALOG_VALUE && toDelete)
						deleteMutation.mutate(toDelete.ProfileInspectionId);
					setToDelete(null);
				}}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
